import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Instagram, ArrowRight, MapPin, Mail, Phone } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Reveal } from "@/components/Reveal";
import { INFO } from "@/data/site";
import { useLang } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TikTok = (props) => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" {...props}>
    <path d="M16.5 3c.3 2.3 1.9 4 4.2 4.3v2.7c-1.5.1-2.9-.4-4.2-1.2v6.1c0 3.4-2.6 6.1-6 6.1s-6-2.7-6-6.1c0-3.3 2.6-6 5.8-6 .3 0 .6 0 .9.1v2.9c-.3-.1-.6-.2-.9-.2-1.7 0-3 1.4-3 3.2 0 1.8 1.3 3.2 3 3.2s3.1-1.4 3.1-3.2V3h3.1z" />
  </svg>
);

export default function Kontakt() {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error(t("kontakt.err_required"));
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(t("kontakt.success"));
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error(t("kontakt.err_generic"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="kontakt-page" className="pt-32 pb-24 md:pb-32">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10 grid lg:grid-cols-2 gap-16">
        <div>
          <Reveal>
            <span className="text-xs uppercase tracking-[0.25em] text-muted-foreground">{t("kontakt.title")}</span>
            <h1 className="font-serif font-light text-5xl md:text-7xl tracking-tighter mt-5">{t("kontakt.hero")}</h1>
            <p className="mt-6 max-w-md text-foreground/70 leading-relaxed">
              {t("kontakt.subtitle")}
            </p>
          </Reveal>

          <div className="mt-12 space-y-6">
            <a href={`mailto:${INFO.email}`} className="flex items-center gap-4 group">
              <span className="w-11 h-11 border border-border flex items-center justify-center group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-300"><Mail strokeWidth={1.25} size={18} /></span>
              <span className="text-foreground/80 group-hover:text-primary transition-colors">{INFO.email}</span>
            </a>
            <a data-testid="kontakt-phone" href={`tel:${INFO.phone}`} className="flex items-center gap-4 group">
              <span className="w-11 h-11 border border-border flex items-center justify-center group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-300"><Phone strokeWidth={1.25} size={18} /></span>
              <span className="text-foreground/80 group-hover:text-primary transition-colors">0176 45665753</span>
            </a>
            <div className="flex items-center gap-4">
              <span className="w-11 h-11 border border-border flex items-center justify-center"><MapPin strokeWidth={1.25} size={18} /></span>
              <span className="text-foreground/80">{INFO.address}</span>
            </div>
            <div className="flex gap-4 pt-2">
              <a data-testid="kontakt-instagram" href={INFO.instagram} target="_blank" rel="noreferrer" className="w-11 h-11 border border-border flex items-center justify-center hover:bg-primary hover:text-primary-foreground transition-colors duration-300"><Instagram strokeWidth={1.25} size={18} /></a>
              <a data-testid="kontakt-tiktok" href={INFO.tiktok} target="_blank" rel="noreferrer" className="w-11 h-11 border border-border flex items-center justify-center hover:bg-primary hover:text-primary-foreground transition-colors duration-300"><TikTok /></a>
            </div>
          </div>
        </div>

        <Reveal delay={0.1}>
          <form onSubmit={submit} className="bg-muted p-8 md:p-12" data-testid="contact-form">
            <div className="space-y-4">
              <Input data-testid="contact-name" placeholder={t("kontakt.name")} value={form.name} onChange={update("name")} className="h-13 rounded-none bg-background border-border py-3" />
              <Input data-testid="contact-email" type="email" placeholder={t("kontakt.email")} value={form.email} onChange={update("email")} className="h-13 rounded-none bg-background border-border py-3" />
              <Textarea data-testid="contact-message" placeholder={t("kontakt.message")} value={form.message} onChange={update("message")} className="rounded-none bg-background border-border min-h-[160px]" />
            </div>
            <button type="submit" data-testid="contact-submit-button" disabled={loading} className="group mt-6 w-full flex items-center justify-between gap-4 bg-primary text-primary-foreground px-7 py-4 disabled:opacity-60">
              <span className="text-sm tracking-wide">{loading ? "…" : t("kontakt.send")}</span>
              <ArrowRight strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </form>
        </Reveal>
      </div>
    </div>
  );
}
